import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import type { ZodType } from "zod";
import { Button } from "@/components/ui/button";
import { Form } from "@/components/ui/form";
import { FormCheckbox, FormInput, FormSelect } from "@/components/forms/FormControls";
import PhoneField from "@/components/forms/PhoneField";
import { useAuth } from "@/lib/auth";
import { ModalCloseButton, ModalOverlay } from "@/components/ui/dialog";
import { DEFAULT_COUNTRY } from "@/lib/phone";
import { orgInviteSchema, dealInviteSchema } from "@/schemas/invites";
import { ORGANIZATION_ROLE_OPTIONS, PARTICIPANT_ROLE_OPTIONS } from "@/constant/organization.constant";
import { InvitationKind, ParticipantRole } from "@/enum/common.enum";
import type { Deal } from "@/types/deals.types";

interface SendInviteModalProps {
  kind: InvitationKind;
  deals: Deal[];
  onClose: () => void;
  onSubmit: (values: Record<string, unknown>) => void | Promise<void>;
}

export default function SendInviteModal({ kind, deals, onClose, onSubmit }: SendInviteModalProps) {
  const isOrg = kind === InvitationKind.ORGANIZATION;
  const schema: ZodType<Record<string, any>> = (isOrg ? orgInviteSchema : dealInviteSchema) as ZodType<Record<string, any>>;
  const form = useForm({
    resolver: zodResolver(schema),
    defaultValues: isOrg
      ? { email: "", role: "agent" }
      : { email: "", deal_id: "", participant_role: "lender", name: "", phone: "", phone_country: DEFAULT_COUNTRY, send_sms: false },
  });

  const participantRole = form.watch("participant_role");
  const sendSms = form.watch("send_sms");
  // `handleSubmit` awaits `onSubmit`, so this stays true for the real duration
  // of the request — the send is not fire-and-forget. Every dismissal path is
  // gated on it: closing the modal mid-flight would drop a form the agent
  // cannot get back if the request then fails.
  const submitting = form.formState.isSubmitting;
  const dismiss = () => { if (!submitting) onClose(); };
  // Same reasoning as the AI Studio tab: when Twilio isn't configured the
  // channel can only produce a skipped send, so hide it rather than offer it.
  const { context } = useAuth();
  // Invitation texts have their own switch, off by default: the A2P campaign
  // only covers users who verified their own number. See SMS_INVITES_ENABLED.
  const smsEnabled = context?.sms_invites_enabled === true;

  return (
    <ModalOverlay onClose={dismiss} testId="new-invite-modal-overlay">
      <Form {...form}>
        <form onClick={(e) => e.stopPropagation()} onSubmit={form.handleSubmit(onSubmit)} className="w-full max-w-md rounded-2xl bg-white p-8 shadow-xl" data-testid="new-invite-modal" noValidate>
          <div className="flex items-start justify-between gap-4">
            <div>
              <h2 className="text-2xl font-bold text-dash-ink">Send an invitation</h2>
              <p className="mt-1 text-sm font-bold text-dash-ink">{isOrg ? "Internal team member" : "External team member"}</p>
            </div>
            <ModalCloseButton onClick={dismiss} disabled={submitting} data-testid="invite-modal-close" />
          </div>

          <div className="-mx-8 my-6 border-t border-dash-border" />

          <div className="space-y-5">
            <FormInput name="email" label="Email" type="email" placeholder="name@brokerage.com" autoComplete="email" required data-testid="invite-email" />
            {isOrg ? (
              <FormSelect name="role" label="Role" options={ORGANIZATION_ROLE_OPTIONS} required data-testid="invite-role" />
            ) : (
              <>
                <FormSelect name="deal_id" label="Deal" placeholder="— select —" options={deals.map((d) => ({ value: d.id, label: d.address }))} required data-testid="invite-deal" />
                <div>
                  <FormSelect name="participant_role" label="Their role on this deal" options={PARTICIPANT_ROLE_OPTIONS} required data-testid="invite-participant-role" />
                  {participantRole === ParticipantRole.CO_AGENT && (
                    <p className="mt-2 text-xs leading-relaxed text-dash-muted">
                      Invites the opposing-side agent into this deal only. They can then add their own client once the deal is under contract.
                    </p>
                  )}
                </div>
                {smsEnabled && (
                  <div className="rounded-xl border border-dash-border p-4">
                    <FormCheckbox
                      name="send_sms"
                      label="Also text them the invitation"
                      description="They still get the email — the text carries the same link."
                      data-testid="invite-send-sms"
                    />
                    {sendSms && (
                      <div className="mt-4">
                        <PhoneField name="phone" countryName="phone_country" required testId="invite-phone" />
                        {/* Consent language is not decoration: this is the opt-in
                            disclosure the A2P 10DLC campaign registration is
                            reviewed against, and it must match what we submit. */}
                        <p className="mt-2 text-xs leading-relaxed text-dash-muted">
                          Only text people who gave you this number and expect to hear from you.
                          They can reply STOP at any time. Message and data rates may apply.
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>

          <div className="-mx-8 mt-8 flex justify-end gap-3 border-t border-dash-border px-8 pt-6">
            <Button type="button" variant="dashboardOutline" size="lg" className="!rounded-xl" onClick={dismiss} disabled={submitting} data-testid="invite-cancel">Cancel</Button>
            <Button type="submit" variant="dashboardPrimary" size="lg" className="!rounded-xl" loading={submitting} disabled={submitting} data-testid="invite-submit">
              {submitting ? (sendSms ? "Sending invitation & text…" : "Sending invitation…") : "Create Invitation"}
            </Button>
          </div>
        </form>
      </Form>
    </ModalOverlay>
  );
}
